import { AsyncPipe } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, OnInit, Renderer2, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ChildrenOutletContexts, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { CleverPushService } from '@zero/feature-clever-push/base';
import { distinctUntilObjectChanged } from '@zero/rxjs-operator';
import { ContractConfirmationFacade } from '@zero/shared/feature-contract-confirmation/base';
import { slideInOutAnimation } from '@zero/shared/ui-animations';
import { FooterComponent, HeaderComponent, SnackbarComponent } from '@zero/shared/ui-web-components';
import { MaintenanceWorkFacade } from '@zero/shared/util-global-state';
import { PushMessageType } from '@zero/shared/util-models';
import { SourcePointEnvironmentConfig, SourcePointService } from '@zero/shared/util-source-point';
import {
  AuthService,
  ClientService,
  DataService,
  MetaDataService,
  PromotionService,
  TrackingService,
  WebviewSupportService,
  WindowService,
} from '@zero/shared/util-web-app-services';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { environment } from 'environments/environment';
import { combineLatest, filter, map, timer } from 'rxjs';

@Component({
  selector: 'zero-root',
  templateUrl: './app.component.html',
  imports: [FooterComponent, HeaderComponent, RouterOutlet, SnackbarComponent, AsyncPipe],
  animations: [slideInOutAnimation],
})
export class AppComponent implements AfterViewInit, OnInit {
  readonly #contractConfirmationFacade = inject(ContractConfirmationFacade);
  readonly #cleverPushService = inject(CleverPushService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #clientService = inject(ClientService);
  readonly #webviewSupportService = inject(WebviewSupportService);
  readonly #router = inject(Router);
  readonly #metaDataService = inject(MetaDataService);
  readonly #renderer = inject(Renderer2);
  readonly #sourcePointService = inject(SourcePointService);
  readonly #trackingService = inject(TrackingService);
  readonly #maintenanceWorkFacade = inject(MaintenanceWorkFacade);
  readonly #authService = inject(AuthService);
  readonly #gtmService = inject(GoogleTagManagerService);
  readonly #promoService = inject(PromotionService);
  readonly #dataService = inject(DataService);
  readonly #windowService = inject(WindowService);
  readonly #contexts = inject(ChildrenOutletContexts);

  public hideHeaderAndFooter$ = this.#activatedRoute.queryParams.pipe(filter(({ hide }) => hide === 'true'));
  public appVersion?: string = undefined;

  public ngOnInit() {
    this.#clientService.determineAppVersion();
    this.#clientService.setBuildVersion(environment.buildVersion);
    this.#webviewSupportService.init();
    this.#loadGtm();
    this.#loadSourcePoint();
    this.#setAppVersion();
    this.#handleMaintenanceWork();
    this.#handleLoginState();
    this.#setupCleverPush();
    this.#handleCleverPushVendor();
    this.#trackingService.listenToTrackingParams();
  }

  public ngAfterViewInit(): void {
    this.#router.events
      .pipe(
        filter((ev) => ev instanceof NavigationEnd),
        map((ev) => ev as NavigationEnd),
      )
      .subscribe((routerEvent) => {
        this.#metaDataService.setMetaTags(routerEvent);
        this.#listenToNewClientVersion(routerEvent.url);
        this.#trackingService.track(routerEvent);
        this.#promoService.storePromotionInfo(this.#activatedRoute.snapshot);
      });
  }

  public getRouteAnimationData() {
    return this.#contexts.getContext('primary')?.route?.snapshot?.data?.['animation'];
  }

  #listenToNewClientVersion(currentUrl: string): void {
    if (!this.#dataService.isNewClientVersionAvailable()) {
      return;
    }

    const disallowedUrls = ['kaufenverkaufen', 'daten/konto'];
    if (!disallowedUrls.some((disallowedUrl) => currentUrl.endsWith(disallowedUrl))) {
      this.#dataService.msgSubject.next({
        type: PushMessageType.msg,
        msg: 'Hinweis',
        msgData: 'Die Seite wird in 3 Sekunden neu geladen, da eine neue Version verfügbar ist.',
        id: crypto.randomUUID(),
        createdDate: new Date(),
      });

      timer(3000).subscribe(() => this.#windowService.getWindow().location.reload());
    }
  }

  #loadGtm(): void {
    this.#gtmService.addGtmToDom().catch(() => {
      console.error('GTM could not be added to DOM');
    });
  }

  async #loadSourcePoint(): Promise<void> {
    const config: SourcePointEnvironmentConfig = {
      privacyManagerId: environment.sourcePoint.web.privacyManagerId,
      propertyHref: environment.sourcePoint.web.propertyHref,
      accountId: environment.sourcePoint.web.accountId,
    };

    try {
      await this.#sourcePointService.init(this.#renderer, config);
      this.#sourcePointService.displayConsentMessage();
      this.#trackingService.init(this.#renderer);
    } catch (e) {
      console.error(e);
    }
  }

  #setAppVersion = (): void => {
    this.appVersion = environment.shouldDisplayAppVersion ? this.#clientService.appVersion : undefined;
  };

  #handleMaintenanceWork = (): void => {
    this.#maintenanceWorkFacade.maintenanceActive$
      .pipe(
        filter((maintenanceActive) => !!maintenanceActive),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.#authService.logoutMaintenance());
  };

  #handleLoginState(): void {
    this.#authService
      .isLoggedIn$()
      .pipe(
        filter((isLoggedIn) => !!isLoggedIn),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.#contractConfirmationFacade.load());
  }

  #setupCleverPush(): void {
    this.#cleverPushService.setup({
      renderer: this.#renderer,
    });
  }

  #handleCleverPushVendor(): void {
    combineLatest({
      isLoggedIn: this.#authService.isLoggedIn$(),
      cleverPushReady: this.#cleverPushService.ready$,
      hasPermission: this.#cleverPushService.hasPermission$,
    })
      .pipe(
        distinctUntilObjectChanged(),
        filter(({ cleverPushReady }) => cleverPushReady),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(({ isLoggedIn, hasPermission }) => {
        if (!hasPermission) {
          this.#cleverPushService.requestPermission();
        } else if (isLoggedIn) {
          this.#cleverPushService.authorize();
        } else {
          this.#cleverPushService.subscribe();
        }
      });
  }
}
